import React, { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import SimpleBar from "simplebar-react";
import FeatherIcon from "feather-icons-react";
import { buildBrokerPath, getBrokerBasePath, useBrokerBasePath } from "./brokerPaths";
import { BROKER_PROFILE_EVENT, BrokerService, clearBrokerProfile, getBrokerProfile, syncBrokerProfile } from "../../pages/Broker/BrokerService";
import { brokerLogoSrc } from "../../util/brokerAuth";

const BROKER_SIDEBAR_STORAGE_KEY = "BrokerSidebarCollapsed";

// Brokers are white-labelled — we never show the FV Bank logo in their portal.
// When a broker has not uploaded a logo we render a monogram from their name.
const getBrokerInitials = (name) => {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "B";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: "grid" },
  { path: "/applications", label: "Applications", icon: "file-text" },
  { path: "/team", label: "Team", icon: "users", requiresTeamManager: true },
  { path: "/settings", label: "Configuration", icon: "settings", requiresTeamManager: true },
];

const BrokerShell = () => {
  const brokerBasePath = useBrokerBasePath();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.localStorage.getItem(BROKER_SIDEBAR_STORAGE_KEY) === "true";
  });

  const [profile, setProfile] = useState(getBrokerProfile);
  const [logoUrl, setLogoUrl] = useState("");

  // Re-read the cached profile when it changes (e.g. logo uploaded in Configuration)
  // so the sidebar branding stays current without a full re-login.
  useEffect(() => {
    const sync = () => setProfile(getBrokerProfile());
    window.addEventListener(BROKER_PROFILE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(BROKER_PROFILE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadBrokerIdentity = async () => {
      const cached = getBrokerProfile() || {};
      // /api/brokers/detail is primary-admin only; calling it for other admins
      // returns 401, which force-logs them out. They keep a generic label.
      if (!cached.isPrimaryAdmin) return;
      if (!cached.brokerId || (cached.brokerName && cached.logo)) return;
      try {
        const response = await BrokerService.brokerDetail(cached.brokerId);
        const broker = response?.data || {};
        if (cancelled) return;
        syncBrokerProfile({
          brokerName: broker.displayName || broker.legalName || cached.brokerName || "",
          logo: brokerLogoSrc(broker) || cached.logo || "",
        });
      } catch {
        // The shell can still render with the cached profile.
      }
    };
    loadBrokerIdentity();
    return () => { cancelled = true; };
  }, []);

  const brokerName = profile?.brokerName || "Broker portal";
  const brokerLogo = profile?.logo;
  const brokerInitials = getBrokerInitials(profile?.brokerName);
  const visibleNavItems = navItems.filter((item) => !item.requiresTeamManager || profile?.canManageTeam);

  useEffect(() => {
    let objectUrl = "";
    let cancelled = false;
    const resolveLogo = async () => {
      if (!brokerLogo) {
        setLogoUrl("");
        return;
      }
      if (!String(brokerLogo).startsWith("/api")) {
        setLogoUrl(brokerLogo);
        return;
      }
      try {
        const token = localStorage.getItem("BearerToken");
        const response = await fetch(brokerLogo, {
          headers: token ? { Authorization: token } : {},
          credentials: "include",
        });
        if (!response.ok) throw new Error("Logo unavailable");
        const blob = await response.blob();
        objectUrl = window.URL.createObjectURL(blob);
        if (!cancelled) setLogoUrl(objectUrl);
      } catch {
        if (!cancelled) setLogoUrl("");
      }
    };
    resolveLogo();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [brokerLogo]);

  const toggleSidebar = () => {
    setIsCollapsed((previous) => {
      const next = !previous;

      if (typeof window !== "undefined") {
        window.localStorage.setItem(BROKER_SIDEBAR_STORAGE_KEY, String(next));
      }

      return next;
    });
  };

  const handleSignOut = () => {
    clearBrokerProfile();
    const loginPath = getBrokerBasePath(window.location.pathname);
    // Hard navigation guarantees a clean state and lands on the broker login,
    // never back on a guarded page like the dashboard.
    window.location.replace(loginPath);
  };

  return (
    <div className={`broker-shell ${isCollapsed ? "broker-shell--collapsed" : ""}`}>
      <aside className={`broker-sidebar ${isCollapsed ? "broker-sidebar--collapsed" : ""}`}>
        <div className={`broker-brand ${brokerLogo ? "broker-brand--logo" : ""}`} title={brokerName}>
          {logoUrl ? (
            // A logo already carries the brand name, so we show it alone (capped to fit)
            // rather than crowding it next to wrapping text.
            <img
              src={logoUrl}
              alt={brokerName}
              className={isCollapsed ? "broker-logo-icon" : "broker-logo"}
            />
          ) : (
            <>
              <span
                className={`broker-brand__monogram ${isCollapsed ? "broker-brand__monogram--sm" : ""}`}
                aria-hidden="true"
              >
                {brokerInitials}
              </span>
              {!isCollapsed && <span className="broker-brand__label">{brokerName}</span>}
            </>
          )}
        </div>
        <SimpleBar className="broker-sidebar__scroll">
          <nav id="broker-sidebar-navigation" aria-label="Broker navigation">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={buildBrokerPath(brokerBasePath, item.path)}
                className={({ isActive }) => `broker-nav-link ${isActive ? "active" : ""}`}
                title={isCollapsed ? item.label : undefined}
              >
                <FeatherIcon icon={item.icon} size={16} />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </nav>
        </SimpleBar>
        <div className="broker-sidebar__footer">
          {!isCollapsed && profile?.email ? (
            <div className="broker-sidebar__profile" title={profile?.email}>
              <span className="broker-sidebar__profile-label">Signed in as</span>
              <span className="broker-sidebar__profile-email">{profile.email}</span>
            </div>
          ) : null}
          <button
            type="button"
            className="broker-sidebar-action broker-sidebar-action--signout"
            onClick={handleSignOut}
            title="Sign out"
            aria-label="Sign out"
          >
            <FeatherIcon icon="log-out" size={16} />
            {!isCollapsed && <span>Sign out</span>}
          </button>
          <button
            type="button"
            className="broker-sidebar-toggle"
            onClick={toggleSidebar}
            aria-controls="broker-sidebar-navigation"
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <FeatherIcon icon={isCollapsed ? "chevron-right" : "chevron-left"} size={16} />
            {!isCollapsed && <span>Collapse sidebar</span>}
          </button>
        </div>
      </aside>
      <main className="broker-main">
        <Outlet />
      </main>
    </div>
  );
};

export default BrokerShell;
